import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fmtMoney, fmtSignedMoney, fmtPct, fmtDate, fmtDateTime, pnlClass, fmtNum } from "@/lib/format";
import { useAccountsSummary, useJournal, useVersions, useAnalytics } from "@/hooks/useData";
import { Sparkline } from "@/components/widgets/StatWidgets";
import { NotebookPen, GitBranch, ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export function DirBadge({ type }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "px-1.5 py-0 text-[10px] uppercase tracking-wide border-transparent",
        type === "buy"
          ? "bg-[hsl(var(--profit))]/12 text-[hsl(var(--profit))]"
          : "bg-[hsl(var(--loss))]/12 text-[hsl(var(--loss))]"
      )}
    >
      {type}
    </Badge>
  );
}

export function RecentTradesList({ data, compact }) {
  const trades = data?.recent_trades || [];
  const cur = data?.account?.currency || "USD";
  if (!trades.length) return <EmptyNote text="No closed trades in this period." />;
  return (
    <div className="-mx-1 overflow-hidden">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-1 pb-1.5 font-medium">Symbol</th>
            <th className="px-1 pb-1.5 font-medium">Side</th>
            {!compact && <th className="px-1 pb-1.5 font-medium text-right">Lots</th>}
            {!compact && <th className="px-1 pb-1.5 font-medium text-right">Pips</th>}
            <th className="px-1 pb-1.5 font-medium text-right">Net P/L</th>
          </tr>
        </thead>
        <tbody>
          {trades.slice(0, compact ? 4 : 9).map((t) => (
            <tr key={t.trade_id} className="border-t border-border/60 hover:bg-muted/40">
              <td className="px-1 py-1.5 font-medium">{t.symbol}</td>
              <td className="px-1 py-1.5"><DirBadge type={t.type} /></td>
              {!compact && <td className="px-1 py-1.5 text-right tabular-nums">{t.lots}</td>}
              {!compact && <td className={cn("px-1 py-1.5 text-right tabular-nums", pnlClass(t.pips))}>{t.pips > 0 ? "+" : ""}{t.pips}</td>}
              <td className={cn("px-1 py-1.5 text-right font-medium tabular-nums", pnlClass(t.net))}>{fmtSignedMoney(t.net, cur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OpenPositionsList({ data, compact }) {
  const positions = data?.open_positions || [];
  const cur = data?.account?.currency || "USD";
  if (!positions.length) return <EmptyNote text="No open positions right now." />;
  return (
    <div className="-mx-1 overflow-hidden">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-1 pb-1.5 font-medium">Symbol</th>
            <th className="px-1 pb-1.5 font-medium">Side</th>
            {!compact && <th className="px-1 pb-1.5 font-medium text-right">Lots</th>}
            {!compact && <th className="px-1 pb-1.5 font-medium text-right">Open</th>}
            <th className="px-1 pb-1.5 font-medium text-right">Floating</th>
          </tr>
        </thead>
        <tbody>
          {positions.slice(0, compact ? 4 : 8).map((p) => (
            <tr key={p.position_id} className="border-t border-border/60 hover:bg-muted/40">
              <td className="px-1 py-1.5 font-medium">{p.symbol}</td>
              <td className="px-1 py-1.5"><DirBadge type={p.type} /></td>
              {!compact && <td className="px-1 py-1.5 text-right tabular-nums">{p.lots}</td>}
              {!compact && <td className="px-1 py-1.5 text-right tabular-nums">{p.open_price}</td>}
              <td className={cn("px-1 py-1.5 text-right font-medium tabular-nums", pnlClass(p.profit))}>{fmtSignedMoney(p.profit, cur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const MOOD_COLOR = {
  confident: "bg-[hsl(var(--profit))]/12 text-[hsl(var(--profit))]",
  neutral: "bg-muted text-muted-foreground",
  frustrated: "bg-[hsl(var(--loss))]/12 text-[hsl(var(--loss))]",
  cautious: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
};

export function JournalListWidget({ shareToken, compact }) {
  const { data: entries, isLoading } = useJournal(shareToken);
  const navigate = useNavigate();
  if (isLoading) return <ListSkeleton />;
  if (!entries?.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-4 text-center">
        <NotebookPen className="h-6 w-6 text-muted-foreground/60" />
        <p className="text-xs text-muted-foreground">No journal entries yet.</p>
        {!shareToken && (
          <Button size="sm" variant="secondary" className="no-drag h-7 text-xs" data-testid="journal-widget-cta"
            onClick={() => navigate("/app/journal")}>
            Write your first entry
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {entries.slice(0, compact ? 2 : 4).map((e) => (
        <div key={e.entry_id} className="rounded-lg border border-border/60 bg-surface-2 px-2.5 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-xs font-medium">{e.title}</span>
            {e.mood && <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] capitalize", MOOD_COLOR[e.mood] || MOOD_COLOR.neutral)}>{e.mood}</span>}
          </div>
          <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">{e.content}</div>
          <div className="mt-1 font-mono text-[10px] text-muted-foreground/70">{fmtDate(e.date)}</div>
        </div>
      ))}
    </div>
  );
}

export function AccountsListWidget({ shareToken, compact }) {
  const { data: rows, isLoading } = useAccountsSummary(shareToken);
  if (isLoading) return <ListSkeleton />;
  const active = (rows || []).filter((r) => r.status !== "pending");
  if (!active.length) return <EmptyNote text="No accounts connected yet." />;
  return (
    <div className="space-y-1.5">
      {active.slice(0, compact ? 3 : 6).map((a) => (
        <div key={a.account_id} className="flex items-center gap-3 rounded-lg border border-border/60 bg-surface-2 px-2.5 py-2" data-testid={`accounts-list-row-${a.account_id}`}>
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium">{a.name}</div>
            <div className="text-[10px] text-muted-foreground">{a.broker} · {a.platform}</div>
          </div>
          {!compact && (
            <Sparkline points={a.sparkline} width={72} height={24}
              className={a.total_gain_pct >= 0 ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]"} stroke="currentColor" />
          )}
          <div className="text-right">
            <div className="text-xs font-semibold tabular-nums">{fmtMoney(a.balance, a.currency)}</div>
            <div className={cn("text-[10px] font-medium tabular-nums", pnlClass(a.total_gain_pct))}>{fmtPct(a.total_gain_pct, true)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function VersionHistoryList({ accountId, shareToken, compact }) {
  const { data: versions, isLoading } = useVersions(accountId, shareToken);
  if (isLoading) return <ListSkeleton />;
  if (!versions?.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1.5 py-4 text-center">
        <GitBranch className="h-6 w-6 text-muted-foreground/60" />
        <p className="text-xs text-muted-foreground">No strategy versions logged for this account.</p>
      </div>
    );
  }
  return (
    <div className="relative space-y-0">
      {versions.slice(0, compact ? 2 : 4).map((v, i, arr) => (
        <div key={v.version_id} className="relative flex gap-2.5 pb-2.5">
          <div className="flex flex-col items-center">
            <span className="mt-1 h-2 w-2 rounded-full bg-primary ring-4 ring-primary/15" />
            {i < arr.length - 1 && <span className="mt-1 w-px flex-1 bg-border" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-semibold text-primary">{v.version}</span>
              <span className="truncate text-xs font-medium">{v.title}</span>
            </div>
            {!compact && <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">{v.notes}</p>}
            <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground/80">
              <span className="font-mono">{fmtDate(v.released_at)}</span>
              {v.impact && <Badge variant="secondary" className="px-1.5 py-0 text-[9px]">{v.impact}</Badge>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const COMPARE_ROWS = [
  { key: "balance", label: "Balance", fmt: (v, c) => fmtMoney(v, c) },
  { key: "total_gain_pct", label: "Total gain", fmt: (v) => fmtPct(v, true), pnl: true },
  { key: "win_rate", label: "Win rate", fmt: (v) => `${v}%` },
  { key: "profit_factor", label: "Profit factor", fmt: (v) => v },
  { key: "max_drawdown", label: "Max drawdown", fmt: (v) => `-${fmtPct(v)}`, invert: true },
  { key: "total_trades", label: "Trades", fmt: (v) => fmtNum(v) },
];

export function AccountComparisonWidget({ settings, shareToken, compact }) {
  const { data: rows, isLoading } = useAccountsSummary(shareToken);
  if (isLoading) return <ListSkeleton />;
  const active = (rows || []).filter((r) => r.status !== "pending");
  if (active.length < 2) return <EmptyNote text="Need at least two accounts to compare." />;
  const a = active.find((r) => r.account_id === settings?.account_a) || active[0];
  const b = active.find((r) => r.account_id === settings?.account_b && r.account_id !== a.account_id)
    || active.find((r) => r.account_id !== a.account_id);
  const list = compact ? COMPARE_ROWS.slice(0, 3) : COMPARE_ROWS;
  return (
    <div>
      <div className="mb-1.5 grid grid-cols-[1fr_auto_auto] gap-x-4 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        <span />
        <span className="max-w-[110px] truncate text-right">{a.name}</span>
        <span className="max-w-[110px] truncate text-right">{b.name}</span>
      </div>
      <div className="space-y-0">
        {list.map((row) => {
          const va = a[row.key];
          const vb = b[row.key];
          const better = row.invert ? (va < vb ? "a" : "b") : va > vb ? "a" : "b";
          return (
            <div key={row.key} className="grid grid-cols-[1fr_auto_auto] gap-x-4 border-t border-border/60 py-1.5 text-xs">
              <span className="text-muted-foreground">{row.label}</span>
              <span className={cn("text-right font-medium tabular-nums", row.pnl && pnlClass(va), better === "a" && "font-semibold")}>
                {row.fmt(va, a.currency)}{better === "a" && <ArrowRight className="ml-1 inline h-3 w-3 rotate-[-45deg] text-[hsl(var(--profit))]" />}
              </span>
              <span className={cn("text-right font-medium tabular-nums", row.pnl && pnlClass(vb), better === "b" && "font-semibold")}>
                {row.fmt(vb, b.currency)}{better === "b" && <ArrowRight className="ml-1 inline h-3 w-3 rotate-[-45deg] text-[hsl(var(--profit))]" />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ListSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-3/4" />
    </div>
  );
}

export function EmptyNote({ text }) {
  return (
    <div className="flex h-full min-h-[64px] items-center justify-center py-3 text-center text-xs text-muted-foreground">
      {text}
    </div>
  );
}

export function ListWidget({ type, data, settings, shareToken, compact }) {
  switch (type) {
    case "recent_trades":
      return <RecentTradesList data={data} compact={compact} />;
    case "open_positions":
      return <OpenPositionsList data={data} compact={compact} />;
    case "journal":
      return <JournalListWidget shareToken={shareToken} compact={compact} />;
    case "accounts_list":
      return <AccountsListWidget shareToken={shareToken} compact={compact} />;
    case "version_history":
      return <VersionHistoryList accountId={settings?.account_id} shareToken={shareToken} compact={compact} />;
    case "account_comparison":
      return <AccountComparisonWidget settings={settings} shareToken={shareToken} compact={compact} />;
    default:
      return null;
  }
}
